import React, { useRef, useState, useEffect } from "react";
import {ICopyComponentInRefDoc, IReferenceDoc, SWTypes} from "interfaces/sw/SWInterfaces";
import "./RefDoc.scoped.scss";
import { useDispatch } from "react-redux";
import { setRefDocLabel, uploadSWAttachment, removeReferenceDoc, setRefDocTableData, setRefDocFilename, } from "store/manageSW/manageSWActions";
import { showErrorToast, showInfoToast } from "store/toast/toastActions";
import { ImageDataDestinations, SWAttachmentTypes } from "store/manageSW/manageSWTypes";
import useSelector from "store/useSelector";
import DeleteIcon from "media/icons/dls/delete.svg";
import CopyIcon from "media/icons/custom/copy-document.svg";
import { addGuidToFilename, getFileSizeError } from "utilities/fileUtilities";
import downloadIcon from "media/icons/dls/download.svg";
import SWApi from "apis/sw/SWApi";
import { getResponseErrorMessage } from "utilities/validationErrorHelpers";
import ModalSpinner from "components/common/ModalSpinner";
import { validateRefDocURL } from "utilities/sw/validateSW";
import ExpandIcon from "media/icons/dls/arrow-right-3.svg";
import CollapseIcon from "media/icons/dls/arrow-down-3.svg";
import Banner, { BannerType } from "components/common/Banner";
import RefDocTableEditor from "./RefDocTableEditor";
import useLocalStorageItemStore from "../../../../utilities/localStorageClipboard";

interface IRefDocProps {
  refDoc: IReferenceDoc,
  disabled: boolean,
}

const RefDoc: React.FC<IRefDocProps> = ({ disabled,
  refDoc,
}) => {
  const {
    SW: {
      guid,
      version,
      type
    },
  } = useSelector(store => store.manageSW);
  const dispatch = useDispatch();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showPopUpForRow, setShowPopUpForRow] = useState(false);
  const [rowReduce, setRowReduce] = useState(refDoc.tableData?.rowCount || 0);
  const [showPopUpForColumn, setShowPopUpForColumn] = useState(false);
  const [colReduce, setColReduce] = useState(refDoc.tableData?.colCount || 0);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [, setCopiedComponent] =useLocalStorageItemStore("authoring.copied-component");

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  useEffect(() => {
    if (rowReduce < (refDoc.tableData?.rowCount || 0) && rowReduce > 0) {
      setShowPopUpForRow(true);
    }
    if (colReduce < (refDoc.tableData?.colCount || 0) && colReduce > 0) {
      setShowPopUpForColumn(true);
    }
  }, [rowReduce, colReduce, refDoc.tableData]);

  const onFileChosen = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files?.length !== 1) {
      return;
    }

    const file = event.target.files[0];

    const fileSizeError = getFileSizeError(file);
    if (fileSizeError) {
      dispatch(showErrorToast(fileSizeError));
      return;
    }

    try {
      addGuidToFilename(file.name);
    } catch (err: any) {
      dispatch(showErrorToast(err.message || err));
      return;
    }

    dispatch(uploadSWAttachment({
      swGuid: guid,
      swVersion: 0,
      file,
      type: SWAttachmentTypes.RefDoc,
      refDocGuid: refDoc.guid,
    }));
  }

  const onDeleteClick = () => {
    dispatch(removeReferenceDoc({
      refDocGuid: refDoc.guid,
    }));
  }

  const downloadRefDoc = async () => {
    try {
      setIsLoading(true);
      const blobUri = await SWApi.getSWBlobURL(guid, version, refDoc.refDocData);
      const link = document.createElement("a");
      link.href = blobUri;
      link.download = refDoc.refDocData;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      // Clean up the object URL
      URL.revokeObjectURL(blobUri);
    } catch (err: any) {
      dispatch(showErrorToast(`Failed to download file: ${getResponseErrorMessage(err)}`));
    } finally {
      setIsLoading(false);
    }
  }

  let onColInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let { valueAsNumber, min, max } = e.target;
    valueAsNumber = Math.max(Number(min), Math.min(Number(max), valueAsNumber));

    if (isNaN(valueAsNumber)) {
      valueAsNumber = Number(min);
    }

    if (valueAsNumber < (refDoc.tableData?.colCount || 0)) {
      let cellsToDelete = refDoc.tableData?.cells.filter(x => x.colIndex === valueAsNumber) || [];
      if (cellsToDelete.find(x => x.value !== undefined)) {
        // only show popup if column has data
        setColReduce(valueAsNumber);
      } else {
        setColReduce(valueAsNumber);
        updateColumn(valueAsNumber);
      }
    }
    else {
      setColReduce(valueAsNumber);
      updateColumn(valueAsNumber);
    }
  }

  let onRowInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let { valueAsNumber, min, max } = e.target;
    valueAsNumber = Math.max(Number(min), Math.min(Number(max), valueAsNumber));

    if (isNaN(valueAsNumber)) {
      valueAsNumber = Number(min);
    }

    if (valueAsNumber < (refDoc.tableData?.rowCount || 0)) {
      let cellsToDelete = refDoc.tableData?.cells.filter(x => x.rowIndex === valueAsNumber) || [];
      if (cellsToDelete.find(x => x.value !== undefined)) {
        // only show popup if row has data.
        setRowReduce(valueAsNumber);
      }
      else {
        setRowReduce(valueAsNumber);
        updateRow(valueAsNumber);
      }
    }
    else {
      setRowReduce(valueAsNumber);
      updateRow(valueAsNumber);
    }
  }

  const updateRow = (rowCount: number) => {
    if (refDoc.tableData) {
      const newTableData = { ...refDoc.tableData };
      if (rowCount <= newTableData?.rowCount) {
        newTableData.cells = [];
        refDoc.tableData?.cells.forEach((item, index, object) => {
          if (item.rowIndex < rowCount)
            newTableData.cells.push(item);
        });
      }

      dispatch(setRefDocTableData({
        refDocGuid: refDoc.guid,
        component: {
          ...newTableData,
          rowCount: rowCount
        }
      }));
    }
  }

  const updateColumn = (colCount: number) => {
    if (refDoc.tableData) {
      const newTableData = { ...refDoc.tableData };
      if (colCount <= newTableData?.colCount) {
        newTableData.cells = [];
        refDoc.tableData?.cells.forEach((item, index, object) => {
          if (item.colIndex < colCount)
            newTableData.cells.push(item);
        });
      }

      dispatch(setRefDocTableData({
        refDocGuid: refDoc.guid,
        component: {
          ...newTableData,
          colCount: colCount
        }
      }));
    }
  }

  const onRowModalClick = (isContinue: boolean) => {
    setShowPopUpForRow(false);
    if (isContinue) {
      updateRow(rowReduce);
    }
    else {
      setRowReduce(refDoc.tableData?.rowCount || 0);
    }
  }

  const onColModalClick = (isContinue: boolean) => {
    setShowPopUpForColumn(false);
    if (isContinue) {
      updateColumn(colReduce);
    }
    else {
      setColReduce(refDoc.tableData?.colCount || 0);
    }
  }

  const setRefDocLink = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setRefDocFilename({
      refDocGuid: refDoc.guid,
      filename: e.target.value,
    }))

    const validationResult = validateRefDocURL(e.target.value);
    if (!validationResult.isValid) {
      dispatch(showErrorToast(validationResult.errorMessage));
    }
  }
    const copyComponentToClipboard = (copyComponent: ICopyComponentInRefDoc) => {
        setCopiedComponent(JSON.stringify({ ...copyComponent }));
        dispatch(showInfoToast("Component copied!"));
    };
  let component: React.ReactElement | undefined;
  if (refDoc.tableData) {
    component = (
      <div className="reference-doc-table">
        <div className="row">
          <img
            src={isCollapsed ? ExpandIcon : CollapseIcon}
            className="icon-small collapse-button hover-gray-bg"
            alt={isCollapsed ? "Expand" : "Collapse"}
            title={isCollapsed ? "Expand Component" : "Collapse Component"}
            onClick={toggleCollapse}
          />
          {!disabled &&
            <img
                src={CopyIcon}
                alt="Copy"
                title="Copy"
                className="copy-button icon-small"
                onClick={() => {
                    copyComponentToClipboard({
                        swGuid: guid,
                        swVersion: version,
                        refDoc: refDoc,
                        refDocType: refDoc.type
                    })
                }}
            />
          }
          {!disabled &&
            <img
              src={DeleteIcon}
              alt="Delete"
              title="Delete"
              className="delete-button icon-small float-right"
              onClick={onDeleteClick}
            />
          }
        </div>
        <div className="row">
          <div className={isCollapsed ? "collapsed" : ""}>
            <RefDocTableEditor
              allowEdit={!disabled}
              refDocGuid={refDoc.guid}
              label={refDoc.label}
              tableComponent={refDoc.tableData}
              swGuid={guid}
              swVersion={version}
              swType={type}
              imageDataDestination={ImageDataDestinations.ManageSW}
              enableTimeUpload={type === SWTypes.MFGSWI || type === SWTypes.MFGCL}
            />
            {isLoading &&
              <ModalSpinner />
            }
          </div>
        </div>
        {isCollapsed && (
          <Banner type={BannerType.Info}>
            <p onClick={toggleCollapse}>
              Table is collapsed. Click <b>here</b> to expand.
            </p>
          </Banner>
        )}
      </div>
    );
  }
  else if (refDoc.type === "Link") {
    component = (
      <div className="reference-doc">
        <div>
          <label className="block mandatory">Link Label
            <span className="compliancelbl"> (Link should be in compliance with SLB security policy)</span>
          </label>
          <input
            type="text"
            defaultValue={refDoc.label}
            disabled={disabled}
            className="label-text"
            onBlur={(e) => dispatch(setRefDocLabel({
              refDocGuid: refDoc.guid,
              label: e.target.value,
            }))}
          />
          <label className="block mandatory">
            URL
          </label>
          <input
            type="text"
            defaultValue={refDoc.refDocData}
            disabled={disabled}
            className="label-text"
            onBlur={setRefDocLink}
          />
          {!!refDoc.label
            && !!refDoc.refDocData
            && <>
              <label className="label-text">Link Preview</label>
              <span>
                <a
                  href={refDoc.refDocData}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {refDoc.label}
                </a>
              </span>
            </>
          }
        </div>
        {!disabled &&
           <img
                src={CopyIcon}
                alt="Copy"
                title="Copy"
                className="copy-button icon-small"
                onClick={() => {
                    copyComponentToClipboard({
                        swGuid: guid,
                        swVersion: version,
                        refDoc: refDoc,
                        refDocType: refDoc.type
                    })
                }}
           />
        }
        {!disabled &&
          <img
            src={DeleteIcon}
            alt="Delete"
            title="Delete"
            className="delete-button icon-small"
            onClick={onDeleteClick}
          />
        }
        {isLoading &&
          <ModalSpinner />
        }
      </div>
    );
  }
  else {
    component = (
      <div className="reference-doc">
        <div>
          <label className="mandatory">
            Label
          </label>
          <input
            type="text"
            className="label-text"
            defaultValue={refDoc.label}
            disabled={disabled}
            onBlur={(e) => dispatch(setRefDocLabel({
              refDocGuid: refDoc.guid,
              label: e.target.value,
            }))}
          />
        </div>
        <div>
          <label className="mandatory">
            File
          </label>
          <div className="file-input-holder">
            <input
              type="file"
              accept="image/*,application/pdf"
              ref={fileInputRef}
              onChange={(e) => onFileChosen(e)}
            />

            <input
              type="text"
              className="filename"
              value={refDoc.refDocData}
              readOnly={true}
              disabled={true}
            />
            <button
              className="primary-button browse"
              disabled={disabled}
              onClick={() => fileInputRef.current?.click()}
            >
              ...
            </button>

            {!!refDoc.refDocData &&
                <button
                  className="secondary-button"
                  onClick={downloadRefDoc}
                  title="Download"
                >
                  <img
                    src={downloadIcon}
                    className="icon-small"
                    alt="Download"
                  />
                </button>
            }
          </div>
        </div>
        {!disabled &&
          <img
            src={DeleteIcon}
            alt="Delete"
            title="Delete"
            className="delete-button icon-small"
            onClick={onDeleteClick}
          />
        }
        {isLoading &&
          <ModalSpinner />
        }
      </div>
    );
  }

  return component;
}

export default RefDoc;